import React, { useCallback, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { FormikProvider, useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ButtonsPanel } from '../ButtonsPanel/ButtonsPanel';
import { stepperInfo } from '../stepperInfo';
import { StepOneFormFields } from './StepOneFormFields';
import { useRidersAutoComplete } from '../../../../hooks/useRidersAutoComplete';
import { RiderHelper } from '../../../../utils/RiderHelper';

import styles from './index.module.css';
import TrackSectionStickiness from '../../../analytics/TrackSectionStickiness ';
import TrackSectionPageView from '../../../analytics/TrackPageView';

function mergeDraftStepOneForForm(step1) {
  if (!step1) return step1;
  const defaults = stepperInfo[0].values;
  const riderInfo = {
    ...defaults.riderInfo,
    ...(step1.riderInfo || {}),
    ...(step1.riderDraft || {}),
  };

  return {
    ...defaults,
    ...step1,
    riderInfo,
  };
}

export const StepOne = ({
  step,
  draft,
  handleSaveAndClose,
  handleSubmit,
  handlePreviousStep,
  showCovidField,
  setFormikValues,
  showSubInformation = true,
  disabledValues,
  delay = 1000,
  quickSchdule = false,
  setStepValid,
  selOrganization = false,
  healthOrganizations,
}) => {
  const location = useLocation();
  const draftIdFromStore = useSelector((state) => state.drafts.draftId);
  const rideDraftKey = draft?.id || draftIdFromStore || '';

  const safeHandleSubmit = useCallback(
    (...args) => (typeof handleSubmit === 'function' ? handleSubmit(...args) : undefined),
    [handleSubmit],
  );

  const formik = useFormik({
    validationSchema: stepperInfo[0].schema(showCovidField),
    enableReinitialize: true,
    initialValues: stepperInfo[0]?.values,
    validateOnMount: true,
    onSubmit: safeHandleSubmit,
  });

  const hasMatchedRiders = useSelector((state) => state.riders.hasMatchedRiders);

  const { validateForm, setValues, values, isValid, setFieldValue } = formik;

  useEffect(() => {
    setStepValid?.(formik.isValid);
  }, [formik.isValid, setStepValid]);

  const onRiderSearch = useCallback(
    (value) => setFieldValue('formattedRiderName', value ?? ''),
    [setFieldValue],
  );

  const { notFoundContent, suggestions, handleSearchRider, handleSelectRider, riderIsPopulated, setRiderIsPopulated } =
    useRidersAutoComplete({
      handleSearch: onRiderSearch,
      value: values?.formattedRiderName,
      delay,
    });

  useEffect(() => {
    if (draft?.step1?.riderDraft?.id) {
      setRiderIsPopulated(true);
    }
  }, [draft?.step1?.riderDraft?.id, setRiderIsPopulated]);

  const hydratedRideDraftKeyRef = useRef(null);

  useEffect(() => {
    if (!rideDraftKey) {
      hydratedRideDraftKeyRef.current = null;
      return;
    }
    if (!draft?.step1) {
      return;
    }
    if (hydratedRideDraftKeyRef.current === rideDraftKey) {
      return;
    }

    hydratedRideDraftKeyRef.current = rideDraftKey;

    const step1 = mergeDraftStepOneForForm(draft.step1);

    void (async () => {
      await setValues(step1);
      await validateForm();
      await setFieldValue('formattedRiderName', RiderHelper.getRiderAutoCompleteName(step1.riderInfo));
    })();
  }, [rideDraftKey, draft?.step1, setFieldValue, setValues, validateForm]);

  useEffect(() => {
    setFormikValues?.(values, isValid, 'step1');
  }, [isValid, values, setFormikValues]);

  const handlePhoneNumberCompleted = useCallback(() => {
    safeHandleSubmit(values, '0');
  }, [safeHandleSubmit, values]);

  return (
    <>
      <TrackSectionStickiness sectionName='Create Ride Step 1' />
      <TrackSectionPageView sectionName='Step 1: Rider Information' pagePath='/rides/create' />
      {showSubInformation && <h2 className={styles.headTitle}>Step 1: Enter Rider Information</h2>}
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} className={styles.riderInfo}>
          <StepOneFormFields
            errors={formik.errors}
            quickSchdule={quickSchdule}
            values={formik.values}
            setFieldValue={setFieldValue}
            showCovidField={showCovidField}
            selOrganization={selOrganization}
            healthOrganizations={healthOrganizations}
            disabledValues={disabledValues}
            notFoundContent={notFoundContent}
            handleSelectRider={handleSelectRider}
            handleSearchRider={handleSearchRider}
            suggestions={suggestions}
            riderIsPopulated={riderIsPopulated}
            setRiderIsPopulated={setRiderIsPopulated}
            onPhoneNumberCompleted={handlePhoneNumberCompleted}
          />

          {showSubInformation && (
            <ButtonsPanel
              disabled={!formik.isValid || hasMatchedRiders}
              step={step}
              handlePreviousStep={handlePreviousStep}
              handleSaveAndClose={handleSaveAndClose}
              handleSubmit={handleSubmit}
              nextButtonText='Save Changes and Next'
            />
          )}
        </form>
      </FormikProvider>
    </>
  );
};

StepOne.propTypes = {
  step: PropTypes.number,
  draft: PropTypes.object,
  handleSaveAndClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  handlePreviousStep: PropTypes.func,
};
